import { escapeRegExp } from 'lodash';
import { ReviewTaskSchema } from '../adapters/reviewTask';
import { TaskSchema } from '../adapters/task';
import {
  ADDRESS_WITH_NAME_REGEX,
  COMMON_WORD_NAMES,
  EMAIL_REGEX,
  GENERAL_SOCIAL_URL_REGEX,
  GENERIC_ADDRESSEES,
  GITHUB_URL_REGEX,
  GREETING_REGEX,
  PARTICLES,
  PHONE_REGEX,
  PROTECTED_SPAN_PATTERNS,
  REDACTED_FIELD,
  REDACTED_LINK,
  ROLE_WORDS,
  SIGNOFF_REGEX,
} from './redactionConstants';

export function redactEmail(text: string): string {
  return text.replace(EMAIL_REGEX, (_match, firstChar, domain) => `${firstChar}***${domain}`);
}

function looksLikeDate(match: string): boolean {
  return /^\d{1,2}[\s.-]\d{1,2}[\s.-](?:19|20)\d{2}$/.test(match);
}

function looksLikeYearSequence(match: string): boolean {
  return /^(?:19|20)\d{2}[\s.-](?:19|20)\d{2}[\s.-](?:19|20)\d{2}$/.test(match);
}

export function redactPhone(text: string): string {
  return text.replace(PHONE_REGEX, match => {
    if (looksLikeDate(match) || looksLikeYearSequence(match)) return match;
    const digits = match.replace(/\D/g, '');
    const last4 = digits.slice(-4);
    return `***-***-${last4}`;
  });
}

export function redactSocialUrl(text: string): string {
  let result = text.replace(GENERAL_SOCIAL_URL_REGEX, REDACTED_LINK);
  result = result.replace(GITHUB_URL_REGEX, (match, _user, restOfPath) =>
    restOfPath ? match : REDACTED_LINK
  );
  return result;
}

function maskNameTokens(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map(word => (PARTICLES.has(word.toLowerCase()) ? word : `${word[0]}***`))
    .join(' ');
}

function isSentenceInitial(text: string, offset: number): boolean {
  const before = text.slice(0, offset);
  return /^\s*$/.test(before) || /[.!?]\s+$/.test(before);
}

export function redactName(text: string, knownNames: string[]): string {
  const names = knownNames.filter(Boolean);
  if (names.length === 0) return text;

  const sortedNames = [...names].sort((a, b) => b.length - a.length);
  const multiWordNames = sortedNames.filter(name => /\s/.test(name));
  const singleWordNames = sortedNames.filter(name => !/\s/.test(name));

  let result = text;

  if (multiWordNames.length > 0) {
    const pattern = multiWordNames.map(escapeRegExp).join('|');
    const regex = new RegExp(`(?<![\\p{L}\\p{N}_])(?:${pattern})(?![\\p{L}\\p{N}_])`, 'giu');
    result = result.replace(regex, match => maskNameTokens(match));
  }

  if (singleWordNames.length > 0) {
    const pattern = singleWordNames.map(escapeRegExp).join('|');
    const regex = new RegExp(`(?<![\\p{L}\\p{N}_])(?:${pattern})(?![\\p{L}\\p{N}_])`, 'gu');
    result = result.replace(regex, (match, offset: number, full: string) => {
      if (COMMON_WORD_NAMES.has(match.toLowerCase())) {
        const followedByComma = full[offset + match.length] === ',';
        if (isSentenceInitial(full, offset) && !followedByComma) {
          return match;
        }
      }
      return maskNameTokens(match);
    });
  }

  return result;
}

function isGenericAddressee(name: string): boolean {
  return name.split(/\s+/).every(token => GENERIC_ADDRESSEES.has(token.toLowerCase()));
}

function redactUnknownGreetingNames(text: string): string {
  return text
    .replace(GREETING_REGEX, (match, name: string) =>
      isGenericAddressee(name) ? match : match.replace(name, maskNameTokens(name))
    )
    .replace(SIGNOFF_REGEX, (match, name: string) =>
      isGenericAddressee(name) ? match : match.replace(name, maskNameTokens(name))
    );
}

function withProtectedSpans(
  text: string,
  patterns: RegExp[],
  transform: (shielded: string) => string
): string {
  const stash: string[] = [];
  let shielded = text;
  patterns.forEach(pattern => {
    shielded = shielded.replace(pattern, match => {
      const token = `\u0000${stash.length}\u0000`;
      stash.push(match);
      return token;
    });
  });

  const result = transform(shielded);
  return stash.reduce(
    (acc, original, index) => acc.replace(`\u0000${index}\u0000`, () => original),
    result
  );
}

interface AddressFields {
  sender?: string;
  receivers?: string[];
  ccs?: string[];
}

interface KnownNamesSource extends AddressFields {
  previousEmails?: AddressFields[];
}

function normalizeDisplayName(raw: string): string {
  const name = raw
    .trim()
    .replace(/^["']|["']$/g, '')
    .trim();
  const commaMatch = name.match(/^([^,]+),\s*(.+)$/);
  return commaMatch ? `${commaMatch[2].trim()} ${commaMatch[1].trim()}` : name;
}

function parseDisplayName(entry: string): string {
  const match = entry.trim().match(ADDRESS_WITH_NAME_REGEX);
  return match ? normalizeDisplayName(match[1]) : '';
}

function deriveNameFromLocalPart(entry: string): string {
  const trimmed = entry.trim();
  if (ADDRESS_WITH_NAME_REGEX.test(trimmed)) return '';
  const localPart = trimmed.replace(/^<|>$/g, '').split('@')[0];
  if (!localPart) return '';
  const parts = localPart.split(/[._+\d]+/).filter(Boolean);
  if (parts.length < 2) return '';
  return parts.map(part => part[0].toUpperCase() + part.slice(1).toLowerCase()).join(' ');
}

function isRoleName(name: string): boolean {
  const tokens = name.split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every(token => ROLE_WORDS.has(token.toLowerCase()));
}

export function extractKnownNames(task: Partial<TaskSchema | ReviewTaskSchema>): string[] {
  const source = task as KnownNamesSource;
  const addressGroups: AddressFields[] = [source, ...(source.previousEmails || [])];

  const entries = addressGroups.flatMap(group => [
    ...(group.sender ? [group.sender] : []),
    ...(group.receivers || []),
    ...(group.ccs || []),
  ]);

  const fullNames = entries
    .map(entry => parseDisplayName(entry) || deriveNameFromLocalPart(entry))
    .filter(Boolean)
    .filter(name => !isRoleName(name));

  const nameTokens = fullNames.flatMap(name =>
    name
      .split(/\s+/)
      .filter(
        token =>
          token.length >= 3 &&
          !PARTICLES.has(token.toLowerCase()) &&
          !ROLE_WORDS.has(token.toLowerCase())
      )
  );

  const uniqueNames = Array.from(new Set([...fullNames, ...nameTokens]));
  return uniqueNames.sort((a, b) => b.length - a.length);
}

export function redactBody(text: string, knownNames: string[] = []): string {
  if (!text) return text;
  let result = redactSocialUrl(text);
  result = redactEmail(result);
  result = redactPhone(result);
  result = withProtectedSpans(result, PROTECTED_SPAN_PATTERNS, shielded => {
    const named = redactName(shielded, knownNames);
    return redactUnknownGreetingNames(named);
  });
  return result;
}

function redactAddressEntry(entry: string, knownNames: string[]): string {
  if (!entry) return entry;
  const match = entry.trim().match(ADDRESS_WITH_NAME_REGEX);
  if (match) {
    const [, rawName, email] = match;
    const normalizedName = rawName ? normalizeDisplayName(rawName) : '';
    const maskedName =
      normalizedName && !isRoleName(normalizedName)
        ? maskNameTokens(normalizedName)
        : normalizedName;
    return maskedName ? `${maskedName} <${redactEmail(email)}>` : `<${redactEmail(email)}>`;
  }
  return withProtectedSpans(redactEmail(entry), PROTECTED_SPAN_PATTERNS, shielded =>
    redactName(shielded, knownNames)
  );
}

type WithExtraFields<T> = T & { contactId?: string; identifier?: string };

export function redactPII<T extends TaskSchema | ReviewTaskSchema>(task: T): T {
  const knownNames = extractKnownNames(task);
  const source = task as WithExtraFields<T>;

  const redacted = {
    ...task,
    sender: redactAddressEntry(task.sender, knownNames),
    receivers: (task.receivers || []).map(r => redactAddressEntry(r, knownNames)),
    ccs: (task.ccs || []).map(c => redactAddressEntry(c, knownNames)),
    subject: redactBody(task.subject, knownNames),
    body: redactBody(task.body, knownNames),
  } as WithExtraFields<T>;

  if (source.contactId) redacted.contactId = REDACTED_FIELD;
  if (source.identifier) redacted.identifier = REDACTED_FIELD;

  const reviewTask = task as unknown as ReviewTaskSchema;
  if (Array.isArray(reviewTask.previousEmails)) {
    (redacted as unknown as ReviewTaskSchema).previousEmails = reviewTask.previousEmails.map(
      email => ({
        ...email,
        sender: redactAddressEntry(email.sender, knownNames),
        receivers: (email.receivers || []).map(r => redactAddressEntry(r, knownNames)),
        ccs: (email.ccs || []).map(c => redactAddressEntry(c, knownNames)),
        subject: redactBody(email.subject, knownNames),
        body: redactBody(email.body, knownNames),
        greeting: redactBody(email.greeting, knownNames),
        farewell: redactBody(email.farewell, knownNames),
        legal: redactBody(email.legal, knownNames),
      })
    );
  }

  const taskWithParsedEmail = task as unknown as TaskSchema;
  if (taskWithParsedEmail.parsedEmail) {
    const parsedEmail = taskWithParsedEmail.parsedEmail;
    (redacted as unknown as TaskSchema).parsedEmail = {
      ...parsedEmail,
      subject: redactBody(parsedEmail.subject, knownNames),
      body: redactBody(parsedEmail.body, knownNames),
      greeting: redactBody(parsedEmail.greeting, knownNames),
      farewell: redactBody(parsedEmail.farewell, knownNames),
      legal: redactBody(parsedEmail.legal, knownNames),
    };
  }

  return redacted as T;
}
